/**
 * The chat's jurisdiction (MAR-1418) — which ONE country's law this conversation is about. Composed
 * by the `web-chat` store facade, which owns `conversationId` and passes it in.
 *
 * Mirrors the chat scope's persistence (`useChatScope.ts`): the pick is saved on the conversation
 * row, rides `createConversation` for a chat that does not exist yet, and an opened conversation's own
 * value always wins. ONE DELIBERATE DIFFERENCE: nothing is remembered in localStorage or
 * sessionStorage. A new chat always starts on the org's main jurisdiction, so choosing another
 * country is a deliberate act every time (Alex, 2026-10-28).
 *
 * The browser's value is a request, never an authority: every turn re-checks the conversation's
 * jurisdiction against the org's current list on the server (`server/services/chat/jurisdiction.ts`).
 */
import { computed, ref, type Ref } from 'vue'
import type { ChatJurisdictionOptions } from '~~/shared/chat/jurisdiction'
import type { ApiFetch } from '~/composables/useApi'

export interface ChatJurisdictionDeps {
  conversationId: Ref<string | null>
  apiFetch: ApiFetch
  /** Persists the pick onto an existing conversation row (the chats store). */
  persistJurisdiction: (conversationId: string, jurisdiction: string | null) => Promise<unknown>
}

export function useChatJurisdiction({ conversationId, apiFetch, persistJurisdiction }: ChatJurisdictionDeps) {
  const jurisdictionOptions = ref<string[]>([])
  const defaultJurisdiction = ref<string>('SV')
  /** What this chat picked; `null` = the org default. */
  const pickedJurisdiction = ref<string | null>(null)
  // conversationId → its row's value, so switching back to a loaded chat adopts it without a fetch.
  const cache = new Map<string, string | null>()
  let loading: Promise<void> | null = null

  /** What the picker shows: the pick if the org still offers it, else the default. */
  const activeJurisdiction = computed(() => {
    const picked = pickedJurisdiction.value
    return picked && jurisdictionOptions.value.includes(picked) ? picked : defaultJurisdiction.value
  })

  /** Fetch the org's options once per page life; a failure leaves no picker (today's composer). */
  function ensureJurisdictionOptions(): Promise<void> {
    loading ??= apiFetch<ChatJurisdictionOptions>('/api/chat/jurisdictions')
      .then((res) => {
        jurisdictionOptions.value = Array.isArray(res?.jurisdictions) ? res.jurisdictions : []
        if (res?.default_jurisdiction) defaultJurisdiction.value = res.default_jurisdiction
      })
      .catch((err) => {
        loading = null
        console.warn('[webChat] could not load the chat jurisdictions:', err)
      })
    return loading
  }

  /** Set when the last save of a pick was refused; the picker says so. Cleared by the next success. */
  const jurisdictionError = ref(false)

  /**
   * What a NEW chat is created with: the pick only while the org still offers it, else nothing (the
   * org default). The options are fetched once per page life, so an admin can remove a country after
   * it was picked — sending it anyway would make the server fall back regardless, and must never
   * cost the user the chat's first message (MAR-1418 audit).
   */
  const jurisdictionForCreate = computed(() => {
    const picked = pickedJurisdiction.value
    return picked && jurisdictionOptions.value.includes(picked) ? picked : null
  })

  /** Persist `code` on `id`; on failure restore `previous` in the UI and the cache, and flag it. */
  async function persistOrRollBack(id: string, code: string | null, previous: string | null): Promise<boolean> {
    try {
      await persistJurisdiction(id, code)
      cache.set(id, code)
      jurisdictionError.value = false
      return true
    } catch (err) {
      console.warn('[webChat] could not save the chat jurisdiction:', err)
      cache.set(id, previous)
      if (conversationId.value === id) pickedJurisdiction.value = previous
      jurisdictionError.value = true
      return false
    }
  }

  /**
   * A user choosing a jurisdiction: persisted when the chat exists, carried by its creation
   * otherwise. Resolves `false` (never rejects) when the save is refused, after rolling back.
   */
  async function setJurisdiction(code: string): Promise<boolean> {
    const id = conversationId.value
    const previous = id ? (cache.get(id) ?? null) : pickedJurisdiction.value
    pickedJurisdiction.value = code
    if (!id) {
      jurisdictionError.value = false
      return true
    }
    cache.set(id, code)
    return persistOrRollBack(id, code, previous)
  }

  /** Drop the once-per-page options and fetch them again. */
  function refreshJurisdictionOptions(): Promise<void> {
    loading = null
    return ensureJurisdictionOptions()
  }

  /**
   * After `createConversation` returns. `stored` is what the row actually holds, which is NOT always
   * what was `sent`: the server re-checks the pick against the org's current list and stores `null`
   * for a country the org has removed since this page fetched its options (MAR-1418 F2). The row is
   * the truth, so the picker adopts it; a mismatch also means the cached options are stale, so they
   * are refetched and the removed country leaves the picker.
   *
   * If the user picked again while the chat was being created (no id yet, so nothing was written),
   * that newer pick is still written now, provided the org still offers it (MAR-1418 audit).
   */
  async function settleCreatedConversation(id: string, sent: string | null, stored: string | null = sent): Promise<void> {
    // Judged against the options the create was sent with, before any refresh.
    const pickedMidCreate = jurisdictionForCreate.value !== sent
    const pickBefore = pickedJurisdiction.value
    cache.set(id, stored)
    if (stored !== sent) await refreshJurisdictionOptions()
    // A pick the user made during that refetch persisted itself (the id exists now) and wins.
    if (pickedJurisdiction.value !== pickBefore) return
    // Only a pick the (possibly refreshed) options still carry is written.
    const now = pickedMidCreate ? jurisdictionForCreate.value : stored
    if (now === stored) {
      if (conversationId.value === id) pickedJurisdiction.value = stored
      return
    }
    await persistOrRollBack(id, now, stored)
  }

  /** Record what a conversation's row holds. */
  function rememberConversationJurisdiction(id: string, value: string | null | undefined) {
    cache.set(id, value ?? null)
  }

  /** An opened conversation's own value wins; one not seen yet starts on the default until loaded. */
  function adoptConversationJurisdiction(id: string, value?: string | null) {
    if (value !== undefined) cache.set(id, value)
    pickedJurisdiction.value = cache.get(id) ?? null
    jurisdictionError.value = false
  }

  /** A new chat: back to the org's main. Never seeded from a previous pick. */
  function resetJurisdiction() {
    pickedJurisdiction.value = null
    jurisdictionError.value = false
  }

  return {
    jurisdictionOptions,
    defaultJurisdiction,
    pickedJurisdiction,
    activeJurisdiction,
    jurisdictionForCreate,
    jurisdictionError,
    ensureJurisdictionOptions,
    setJurisdiction,
    settleCreatedConversation,
    rememberConversationJurisdiction,
    adoptConversationJurisdiction,
    resetJurisdiction,
  }
}
